/**
 * WorkflowLaunchModal — DRY workflow launch with 5W1H context capture
 *
 * Three launch views, all sharing the same createInstance → startInstance path:
 *
 *   Quick    — auto-start, no context (admin/system workflows)
 *   Standard — Subject (Who) + Where (location) + When (scheduled) + name
 *   Detailed — Standard + autoStart toggle + priority + inputData override
 *
 * Override for unique workflows: workflows declare `required_launch_context`
 * in their definition; the Standard view marks those fields required.
 * Until the backend exposes that field on GqlWorkflow, we default to optional.
 */

import { useState, useCallback, useMemo } from 'react';
import {
  Modal,
  Stack,
  Group,
  Text,
  Button,
  TextInput,
  Switch,
  Alert,
  Badge,
  ActionIcon,
  Tooltip,
  SegmentedControl,
  ScrollArea,
  Divider,
  Loader,
  Center,
  Box,
  Anchor,
  Select,
  NumberInput,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import {
  IconRocket,
  IconPlus,
  IconAlertTriangle,
  IconExternalLink,
  IconPlayerPlay,
  IconCheck,
  IconX,
  IconClock,
  IconCircleDashed,
  IconUser,
  IconMapPin,
  IconCalendar,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { gql } from '@apollo/client';
import { useMutation as useGqlMutation } from '@apollo/client/react';
import {
  useCreateInstanceMutation,
  useStartInstanceMutation,
  useListInstancesByWorkflowQuery,
  useListActorsQuery,
  GqlInstanceStatus,
  GqlActorType,
  type Instance,
} from '@/generated/graphql';
import type { PrimitiveItem } from '@/hooks/usePrimitiveData';

// DRR-182: Draft instance mutations (Valkey-only drafts with reservation)
const CREATE_DRAFT_INSTANCE = gql`
  mutation CreateDraftInstance($input: GqlCreateInstanceInput!) {
    createDraftInstance(input: $input) {
      draftId existingId created message
    }
  }
`;
const COMMIT_DRAFT_INSTANCE = gql`
  mutation CommitDraftInstance($draftId: String!) {
    commitDraftInstance(draftId: $draftId) { id status currentState instanceName }
  }
`;

// ============================================================================
// TYPES
// ============================================================================

interface WorkflowLaunchModalProps {
  opened: boolean;
  onClose: () => void;
  workflowItem: PrimitiveItem | null;
  tenantId: string;
}

type LaunchView = 'quick' | 'standard' | 'detailed';

// ============================================================================
// STATUS CONFIG
// ============================================================================

const STATUS_CONFIG: Record<string, { color: string; icon: typeof IconPlayerPlay }> = {
  [GqlInstanceStatus.RUNNING]: { color: 'blue', icon: IconPlayerPlay },
  [GqlInstanceStatus.COMPLETED]: { color: 'green', icon: IconCheck },
  [GqlInstanceStatus.FAILED]: { color: 'red', icon: IconX },
  [GqlInstanceStatus.PENDING]: { color: 'gray', icon: IconCircleDashed },
  [GqlInstanceStatus.PAUSED]: { color: 'yellow', icon: IconClock },
  [GqlInstanceStatus.CANCELLED]: { color: 'gray', icon: IconX },
  [GqlInstanceStatus.TIMED_OUT]: { color: 'orange', icon: IconClock },
  [GqlInstanceStatus.COMPENSATING]: { color: 'orange', icon: IconClock },
  [GqlInstanceStatus.COMPENSATED]: { color: 'teal', icon: IconCheck },
};

const ACTIVE_STATUSES = new Set<GqlInstanceStatus>([GqlInstanceStatus.RUNNING, GqlInstanceStatus.PENDING, GqlInstanceStatus.PAUSED]);

// Actor types that can be a workflow subject
const SUBJECT_ACTOR_TYPES = new Set<string>([GqlActorType.HUMAN, GqlActorType.SUBJECT]);

function formatRelative(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function WorkflowLaunchModal({
  opened,
  onClose,
  workflowItem,
  tenantId,
}: WorkflowLaunchModalProps) {
  const navigate = useNavigate();
  const [launchView, setLaunchView] = useState<LaunchView>('quick');

  // ── Universal fields (all views) ──────────────────────────────────────────
  const [launchName, setLaunchName] = useState('');

  // ── Standard 5W1H fields ──────────────────────────────────────────────────
  // WHO  — subject actor (prospective resident, patient, claimant, etc.)
  const [subjectActorId, setSubjectActorId] = useState<string | null>(null);
  // WHERE — physical/logical location or constellation ref
  const [location, setLocation] = useState('');
  // WHEN — scheduled execution start
  const [scheduledDate, setScheduledDate] = useState<Date | null>(null);

  // ── Detailed-only fields ──────────────────────────────────────────────────
  const [autoStart, setAutoStart] = useState(true);
  const [priority, setPriority] = useState<number | string>(50);

  const [launchError, setLaunchError] = useState<string | null>(null);
  const [launchLoading, setLaunchLoading] = useState(false);

  const [createInstance] = useCreateInstanceMutation();
  const [startInstance] = useStartInstanceMutation();
  const [createDraft] = useGqlMutation<{ createDraftInstance: { draftId?: string; existingId?: string; created: boolean; message?: string } }>(CREATE_DRAFT_INSTANCE);
  const [commitDraft] = useGqlMutation<{ commitDraftInstance: { id: string; status: string; currentState: string; instanceName?: string } }>(COMMIT_DRAFT_INSTANCE);

  // Load actors for Subject picker — shown only in standard/detailed
  const { data: actorsData } = useListActorsQuery({
    variables: { limit: 200 },
    skip: !opened || launchView === 'quick',
  });

  const actorSelectData = useMemo(() => {
    const items = actorsData?.listActors?.items ?? [];
    return items
      .filter(a => SUBJECT_ACTOR_TYPES.has(a.actorType as GqlActorType))
      .map(a => ({
        value: a.id,
        label: `${a.name} (${a.actorType})`,
      }));
  }, [actorsData]);

  // Fetch run history for this workflow
  const { data: historyData, loading: historyLoading } = useListInstancesByWorkflowQuery({
    variables: {
      tenantId,
      workflowDefinitionId: workflowItem?.id as string,
      limit: 20,
    },
    skip: !opened || !workflowItem?.id || !tenantId,
    fetchPolicy: 'cache-and-network',
  });

  const instances: Instance[] = historyData?.instanceApiListInstancesByWorkflow?.items ?? [];
  const activeInstances = instances.filter((i: Instance) => ACTIVE_STATUSES.has(i.status as GqlInstanceStatus));
  const hasActive = activeInstances.length > 0;

  const handleClose = useCallback(() => {
    setLaunchName('');
    setLaunchError(null);
    setSubjectActorId(null);
    setLocation('');
    setScheduledDate(null);
    setAutoStart(true);
    setPriority(50);
    setLaunchView('quick');
    onClose();
  }, [onClose]);

  // DRR-182: Draft flow — createDraft (Valkey) → reservation check → commitDraft (DDB)
  // Falls back to legacy createInstance → startInstance if draft mutations unavailable.
  const handleSubmit = useCallback(async () => {
    if (!workflowItem || !tenantId) return;
    // Guard: prevent double-submit from React StrictMode or rapid clicks
    if (launchLoading) return;
    setLaunchError(null);
    setLaunchLoading(true);
    const input = {
      workflowDefinitionId: workflowItem.id as string,
      instanceName: launchName.trim() || undefined,
      inputData: {},
      subjectActorId: subjectActorId ?? undefined,
      metadata: location.trim() ? { location: location.trim() } : undefined,
      scheduledStartTime: scheduledDate ? scheduledDate.toISOString() : undefined,
      priority: launchView === 'detailed' ? Number(priority) : undefined,
    };
    try {
      // Try draft flow first (DRR-182)
      const { data: draftResult } = await createDraft({ variables: { input } });
      const draft = draftResult?.createDraftInstance;

      if (draft && !draft.created && draft.existingId) {
        // Reservation conflict — active instance exists for this subject+workflow
        const goToExisting = confirm(
          `${draft.message}\n\nOpen the existing instance instead?`
        );
        if (goToExisting) {
          handleClose();
          navigate(`/ferp-core/instances/${draft.existingId}`);
          return;
        }
        setLaunchError('Cancel the existing instance first, then try again.');
        return;
      }

      if (draft?.draftId) {
        // Auto-commit: Valkey draft → DDB
        const { data: commitResult } = await commitDraft({
          variables: { draftId: draft.draftId },
        });
        const committed = commitResult?.commitDraftInstance;
        let instanceId = committed?.id;

        // Start the instance → triggers entry actions → spawns tasks
        if (instanceId) {
          const shouldStart = launchView === 'detailed' ? autoStart : true;
          if (shouldStart) {
            try {
              const startResult = await startInstance({
                variables: {
                  tenantId,
                  instanceId,
                  workflowDefinitionId: input.workflowDefinitionId,
                },
              });
              const startedId = startResult.data?.instanceApiStartInstance?.id;
              if (startedId) instanceId = startedId;
            } catch (startErr) {
              console.warn('Instance start failed (can activate manually):', startErr);
            }
          }
        }

        handleClose();
        if (instanceId) {
          navigate(`/ferp-core/instances/${instanceId}`);
        }
        return;
      }

      // Draft returned but no draftId and no reservation conflict — unexpected state
      if (draft) {
        setLaunchError(draft.message || 'Draft creation returned unexpected result.');
        return;
      }

      // Fallback: legacy path (only if draft mutation returned null — not deployed yet)
      const { data: result } = await createInstance({
        variables: { tenantId, input },
      });
      const newInstance = result?.instanceApiCreateInstance;
      const shouldStart = launchView === 'detailed' ? autoStart : true;
      let runningInstanceId = newInstance?.id;
      if (newInstance && shouldStart) {
        const startResult = await startInstance({
          variables: {
            tenantId,
            instanceId: newInstance.id,
            workflowDefinitionId: workflowItem.id as string,
          },
        });
        const startedId = startResult.data?.instanceApiStartInstance?.id;
        if (startedId) runningInstanceId = startedId;
      }
      handleClose();
      if (runningInstanceId) {
        navigate(`/ferp-core/instances/${runningInstanceId}`);
      }
    } catch (e: unknown) {
      setLaunchError(e instanceof Error ? e.message : String(e));
    } finally {
      setLaunchLoading(false);
    }
  }, [
    workflowItem, tenantId, launchName, subjectActorId, location,
    scheduledDate, autoStart, priority, launchView,
    createDraft, commitDraft, createInstance, startInstance, handleClose, navigate,
  ]);

  const isStandard = launchView === 'standard' || launchView === 'detailed';

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs" align="center">
          <Text fw={600} size="sm">Launch Workflow Instance</Text>
          <Tooltip label={launchView === 'quick' ? 'Add 5W1H context' : 'Switch view'} position="right">
            <ActionIcon
              size="xs"
              variant={launchView !== 'quick' ? 'filled' : 'subtle'}
              color={launchView !== 'quick' ? 'blue' : 'gray'}
              onClick={() =>
                setLaunchView(v =>
                  v === 'quick' ? 'standard' : v === 'standard' ? 'detailed' : 'quick'
                )
              }
            >
              <IconPlus size={12} />
            </ActionIcon>
          </Tooltip>
        </Group>
      }
      size="lg"
      zIndex={1100}
    >
      <Stack gap="md">
        {/* Workflow name */}
        <Text size="sm" c="dimmed">
          Run <strong>{workflowItem?.name}</strong>
        </Text>

        {/* Active instance warning */}
        {hasActive && (
          <Alert
            color="yellow"
            icon={<IconAlertTriangle size={16} />}
            title={`${activeInstances.length} active instance${activeInstances.length > 1 ? 's' : ''} already running`}
          >
            <Stack gap={4}>
              {activeInstances.slice(0, 3).map(inst => (
                <Group key={inst.id} gap="xs">
                  <Badge size="xs" color={STATUS_CONFIG[inst.status]?.color ?? 'gray'}>
                    {inst.status}
                  </Badge>
                  <Text size="xs">{inst.instanceName ?? inst.id}</Text>
                  <Anchor
                    size="xs"
                    onClick={() => { handleClose(); navigate(`/ferp-core/workflows/${workflowItem?.id ?? ''}?instance=${inst.id}`); }}
                  >
                    View <IconExternalLink size={10} style={{ verticalAlign: 'middle' }} />
                  </Anchor>
                </Group>
              ))}
            </Stack>
          </Alert>
        )}

        {launchError && (
          <Alert color="red" title="Launch failed">{launchError}</Alert>
        )}

        {/* View selector */}
        {launchView !== 'quick' && (
          <SegmentedControl
            size="xs"
            value={launchView}
            onChange={v => setLaunchView(v as LaunchView)}
            data={[
              { value: 'quick', label: 'Quick' },
              { value: 'standard', label: 'Standard' },
              { value: 'detailed', label: 'Detailed' },
            ]}
          />
        )}

        {/* ── Standard view: 5W1H context ──────────────────────────────── */}
        {isStandard && (
          <>
            {/* WHO — subject actor */}
            <Select
              label="Subject (Who)"
              description="Person this workflow is about — resident, patient, claimant, etc."
              placeholder="Search actors…"
              leftSection={<IconUser size={14} />}
              value={subjectActorId}
              onChange={setSubjectActorId}
              data={actorSelectData}
              searchable
              clearable
              nothingFoundMessage="No matching actors"
            />

            {/* WHERE — location / constellation */}
            <TextInput
              label="Location (Where)"
              description="Facility, wing, room, or constellation ref — e.g. wing-a, constellation-care-service-plan"
              placeholder="e.g. wing-a-room-102, facility-hillside"
              leftSection={<IconMapPin size={14} />}
              value={location}
              onChange={e => setLocation(e.currentTarget.value)}
            />

            {/* WHEN — scheduled start */}
            <DatePickerInput
              label="Scheduled For (When)"
              description="Target start date — leave blank to start now"
              placeholder="Pick a date"
              leftSection={<IconCalendar size={14} />}
              value={scheduledDate}
              onChange={(value: string | null) => setScheduledDate(value ? new Date(value) : null)}
              clearable
              minDate={new Date()}
            />

            {/* Instance name */}
            <TextInput
              label="Instance Name"
              description="Optional — defaults to workflow name + timestamp"
              placeholder="e.g. Smith, Jane — Admission 2026-04"
              value={launchName}
              onChange={e => setLaunchName(e.currentTarget.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }}
            />
          </>
        )}

        {/* ── Detailed-only overrides ───────────────────────────────────── */}
        {launchView === 'detailed' && (
          <>
            <NumberInput
              label="Priority"
              description="0 (lowest) – 255 (highest). Default: 50"
              min={0}
              max={255}
              value={priority}
              onChange={setPriority}
            />
            <Switch
              label="Start immediately after creation"
              description="Off = Pending state, manual Start required"
              checked={autoStart}
              onChange={e => setAutoStart(e.currentTarget.checked)}
            />
          </>
        )}

        {/* 5W1H coverage indicator */}
        {isStandard && (
          <Group gap={6}>
            <Text size="xs" c="dimmed">5W1H coverage:</Text>
            <Badge size="xs" color="gray" variant="outline">What ✓</Badge>
            <Badge size="xs" color={subjectActorId ? 'green' : 'gray'} variant="outline">
              Who {subjectActorId ? '✓' : '—'}
            </Badge>
            <Badge size="xs" color={location ? 'green' : 'gray'} variant="outline">
              Where {location ? '✓' : '—'}
            </Badge>
            <Badge size="xs" color={scheduledDate ? 'green' : 'gray'} variant="outline">
              When {scheduledDate ? '✓' : '—'}
            </Badge>
            <Badge size="xs" color="blue" variant="outline">Why auto</Badge>
            <Badge size="xs" color="blue" variant="outline">How auto</Badge>
          </Group>
        )}

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={handleClose} disabled={launchLoading}>Cancel</Button>
          <Button
            color="green"
            leftSection={<IconRocket size={14} />}
            loading={launchLoading}
            onClick={handleSubmit}
          >
            {launchView === 'detailed' && !autoStart ? 'Create (Pending)' : 'Launch & Start'}
          </Button>
        </Group>

        {/* ------------------------------------------------------------------ */}
        {/* Run History                                                         */}
        {/* ------------------------------------------------------------------ */}
        <Divider label={
          <Group gap={6}>
            <Text size="xs" c="dimmed">Previous Runs</Text>
            {instances.length > 0 && (
              <Badge size="xs" variant="light" color="gray">{instances.length}</Badge>
            )}
          </Group>
        } labelPosition="left" />

        {historyLoading && instances.length === 0 ? (
          <Center h={60}><Loader size="sm" /></Center>
        ) : instances.length === 0 ? (
          <Text size="xs" c="dimmed" ta="center" py="sm">No previous runs for this workflow.</Text>
        ) : (
          <ScrollArea h={220}>
            <Stack gap={4}>
              {instances.map(inst => {
                const cfg = STATUS_CONFIG[inst.status] ?? { color: 'gray', icon: IconCircleDashed };
                const StatusIcon = cfg.icon;
                const isActive = ACTIVE_STATUSES.has(inst.status as GqlInstanceStatus);
                return (
                  <Box
                    key={inst.id}
                    style={{
                      padding: '6px 10px',
                      borderRadius: 'var(--mantine-radius-sm)',
                      border: '1px solid var(--mantine-color-default-border)',
                      background: isActive ? 'var(--mantine-color-blue-light)' : 'transparent',
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      handleClose();
                      navigate(`/ferp-core/workflows/${workflowItem?.id ?? ''}?instance=${inst.id}`);
                    }}
                  >
                    <Group justify="space-between" gap="xs">
                      <Group gap="xs" style={{ flex: 1, minWidth: 0 }}>
                        <StatusIcon size={14} color={`var(--mantine-color-${cfg.color}-6)`} />
                        <Text size="xs" fw={500} truncate style={{ maxWidth: 200 }}>
                          {inst.instanceName ?? inst.id}
                        </Text>
                        <Badge size="xs" color={cfg.color} variant="light">{inst.status}</Badge>
                        {inst.progressPercent != null && inst.progressPercent > 0 && inst.progressPercent < 100 && (
                          <Text size="xs" c="dimmed">{Math.round(inst.progressPercent)}%</Text>
                        )}
                      </Group>
                      <Group gap={6} wrap="nowrap">
                        {inst.currentState && (
                          <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                            {inst.currentState}
                          </Text>
                        )}
                        <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                          {formatRelative(inst.createdAt as string)}
                        </Text>
                        <ActionIcon
                          size="xs"
                          variant="subtle"
                          color="blue"
                          onClick={e => {
                            e.stopPropagation();
                            handleClose();
                            navigate(`/ferp-core/workflows/${workflowItem?.id ?? ''}?instance=${inst.id}`);
                          }}
                        >
                          <IconExternalLink size={12} />
                        </ActionIcon>
                      </Group>
                    </Group>
                  </Box>
                );
              })}
            </Stack>
          </ScrollArea>
        )}
      </Stack>
    </Modal>
  );
}
